/**
 * Step 4 — Intent & acquisition source.
 * Captures: primary_intents (1..3), acquisition_source, referral_code.
 */
import React, { useState } from 'react';
import { Loader2, Check } from 'lucide-react';
import { useSignupTelemetry } from '../useSignupTelemetry';
import {
  IntentAndSourceStepPayload,
  PrimaryIntent,
  AcquisitionSource,
} from '../signupApi';

interface Props {
  onAdvance: () => void;
  onBack: () => void;
}

const INTENT_OPTIONS: { value: PrimaryIntent; label: string; desc: string }[] = [
  { value: 'case_research',      label: 'Case research',          desc: 'Search judgments, find precedents' },
  { value: 'drafting_documents', label: 'Drafting documents',     desc: 'Petitions, contracts, notices' },
  { value: 'client_management',  label: 'Client & matter management', desc: 'Organise cases, deadlines, vault' },
  { value: 'precedent_search',   label: 'Precedent search',       desc: 'Find supporting/distinguishing cases' },
  { value: 'bns_ipc_mapping',    label: 'IPC ↔ BNS mapping',      desc: 'New criminal-code conversions' },
  { value: 'bail_calculation',   label: 'Bail strategy',          desc: 'Eligibility, precedents, drafting' },
  { value: 'fir_analysis',       label: 'FIR analysis',           desc: 'Triage and defense angles' },
  { value: 'learning_new_laws',  label: 'Learning new laws',      desc: 'Stay updated on BNS/BNSS/BSA' },
  { value: 'other',              label: 'Something else',         desc: '' },
];

const SOURCE_OPTIONS: { value: AcquisitionSource; label: string }[] = [
  { value: 'google_search',    label: 'Google search' },
  { value: 'referral_friend',  label: 'A friend or colleague' },
  { value: 'social_linkedin',  label: 'LinkedIn' },
  { value: 'social_twitter',   label: 'Twitter / X' },
  { value: 'bar_association',  label: 'Bar association / chamber' },
  { value: 'legal_blog',       label: 'A legal blog or publication' },
  { value: 'youtube',          label: 'YouTube' },
  { value: 'event_conference', label: 'An event / conference' },
  { value: 'other',            label: 'Other' },
];

const MAX_INTENTS = 3;

const IntentAndSourceStep: React.FC<Props> = ({ onAdvance, onBack }) => {
  const [intents, setIntents] = useState<PrimaryIntent[]>([]);
  const [source, setSource] = useState<AcquisitionSource | ''>('');
  const [referral, setReferral] = useState<string>('');
  const { complete, skip, pending, error } = useSignupTelemetry({
    stepName: 'intent_and_source',
    onAdvance,
  });

  const toggleIntent = (i: PrimaryIntent) => {
    setIntents((prev) => {
      if (prev.includes(i)) return prev.filter((x) => x !== i);
      if (prev.length >= MAX_INTENTS) return prev;
      return [...prev, i];
    });
  };

  const canSubmit = intents.length >= 1 && intents.length <= MAX_INTENTS && source;

  const submit = () => {
    if (!canSubmit) return;
    const payload: IntentAndSourceStepPayload = {
      primary_intents: intents,
      acquisition_source: source as AcquisitionSource,
      referral_code: referral.trim() || null,
    };
    complete(payload as unknown as Record<string, unknown>);
  };

  return (
    <div className="bg-surface-elevated rounded-xl border border-border-primary p-6 shadow-elevation-1">
      <div className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1.5">
            What do you most want to do here? <span className="text-crimson-500">*</span>
          </label>
          <p className="text-xs text-text-tertiary mb-3">
            Pick up to {MAX_INTENTS}. We'll prioritise these on your home screen.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {INTENT_OPTIONS.map((opt) => {
              const selected = intents.includes(opt.value);
              const disabled = !selected && intents.length >= MAX_INTENTS;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => toggleIntent(opt.value)}
                  disabled={disabled}
                  className={`text-left rounded-lg border p-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    selected
                      ? 'border-legal-green-500 bg-legal-green-50 dark:bg-legal-green-900/20'
                      : 'border-border-primary bg-surface hover:border-legal-green-400/50'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <div className={`mt-0.5 h-4 w-4 rounded border flex items-center justify-center shrink-0 ${
                      selected
                        ? 'bg-legal-green-500 border-legal-green-500'
                        : 'border-border-primary bg-surface'
                    }`}>
                      {selected && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-text-primary">{opt.label}</div>
                      {opt.desc && <div className="text-xs text-text-tertiary">{opt.desc}</div>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1.5">
            How did you hear about iAdvocate? <span className="text-crimson-500">*</span>
          </label>
          <select
            value={source}
            onChange={(e) => setSource(e.target.value as AcquisitionSource)}
            className="w-full rounded-lg border border-border-primary bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-legal-green-500"
          >
            <option value="">Select…</option>
            {SOURCE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1.5">
            Referral code
          </label>
          <p className="text-xs text-text-tertiary mb-2">Optional — leave blank if none.</p>
          <input
            type="text"
            value={referral}
            onChange={(e) => setReferral(e.target.value)}
            placeholder="ABCD2026"
            className="w-full rounded-lg border border-border-primary bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-legal-green-500"
          />
        </div>

        {error && (
          <div className="p-3 bg-crimson-50 dark:bg-crimson-900/20 border border-crimson-200 dark:border-crimson-800 rounded-lg text-crimson-700 dark:text-crimson-400 text-sm">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <button
            onClick={skip}
            disabled={pending}
            className="text-sm text-text-tertiary hover:text-text-secondary disabled:opacity-50"
          >
            Skip for now
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit || pending}
            className="px-6 py-2.5 bg-legal-green-500 text-white font-semibold rounded-lg hover:bg-legal-green-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm shadow-elevation-1"
          >
            {pending ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving…
              </span>
            ) : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IntentAndSourceStep;
